import type { ChecklistSeverity, ChecklistCategory, ChecklistType } from '@arhs-fleetback/types/entities/checklists';
import { differenceInMilliseconds } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';

import { hasAccess } from 'components/common/utils/hasAccess';
import {
  SEVERITY,
  ADVICE_STATUS,
  CHECKLIST_DONE,
  CHECKLIST_STATUS,
  CHECKLIST_DISCOUNT,
} from 'constants/ChecklistConstants';
import { DEFAULT_LANGUAGE } from 'constants/LanguageConstants';
import { TECHNICIAN } from 'constants/ModeConstants';
import { SALES_MANAGER, AFTER_SALES_MANAGER } from 'constants/UserConstants';
import type { Checklist } from 'modules/checklists/types/checklist';
import type { InterventionChecklistAdvice } from 'modules/checklists/types/resources/advices/intervention-checklist-advice';
import type { ChecklistItem } from 'modules/checklists/types/resources/items/checklist-item';
import type { Label } from 'modules/global/typing/common/Label';
import type { ChecklistGroup } from 'modules/interventions/types/projections/detailed-intervention';
import type { AbstractMedia } from 'modules/medias/types/abstract-media';
import { DEFAULT_PAYER_ID } from 'modules/payers/constants';
import { compact } from 'utils/ObjectUtils';

/**
 * These utilities operate on react-final-form *working copies* of intervention checklists, not on
 * raw API projections: the form tree carries extra client-only fields (`key`, `isNew`, `index`,
 * `paths`, `checklistId`…) and enriches advices with the parent checklist `type`. The types below
 * model those working shapes; they build on the domain enums / `Label` from `modules/checklists`.
 */

/** Dot-notated react-final-form paths pointing to a checklist / item / advice in the form tree. */
export type ChecklistPaths = {
  checklists?: string;
  checklist: string;
  categories: string;
  category: string;
  advices: string;
  advice: string;
};

/**
 * A checklist advice as manipulated in the intervention forms: a partial {@link InterventionChecklistAdvice}
 * (the working copy rarely carries every API field) enriched with client-only fields. The overridden fields
 * below diverge from the API projection.
 */
export type ChecklistAdviceForm = Omit<Partial<InterventionChecklistAdvice>, 'type' | 'adviceName' | 'category'> & {
  /** May still be a plain string while being edited, before being turned into a Label. */
  adviceName?: Label | string;
  category?: ChecklistCategory | string;
  /** Enriched with the *parent checklist* type, not the advice discriminator type. */
  type?: ChecklistType;
  /** Client-only fields carried by the react-final-form working copy. */
  isNew?: boolean;
  index?: number;
  checklistId?: string;
  categoryId?: string;
  allowRestrict?: boolean;
  paths?: ChecklistPaths;
};

/** A checklist item (category) as manipulated in the forms. */
export type ChecklistItemForm = Omit<Partial<ChecklistItem>, 'advices' | 'categoryName'> & {
  advices: ChecklistAdviceForm[];
  categoryName?: Label | string;
};

/**
 * A checklist as manipulated in the intervention forms: a partial {@link Checklist} (client-side copies omit
 * the server audit fields) plus the client-only / intervention-specific fields carried in the form tree.
 */
export type ChecklistForm = Omit<Partial<Checklist>, 'items' | 'name'> & {
  items: ChecklistItemForm[];
  name?: Label | string;
  key?: string;
  interventionId?: string;
  dealerId?: string;
  groupId?: string;
  checklistGroupId?: string;
  selfServiceId?: string;
  offeredChecklist?: boolean;
  originChecklistId?: string;
  originInterventionId?: string;
  originTireScanId?: string;
  originAuditId?: string;
  originTireReportId?: string;
};

/** Transform callbacks used by formatImportChecklists. */
export type ChecklistFormatters = {
  checklist?: (checklist: ChecklistForm) => ChecklistForm;
  item?: (item: ChecklistItemForm) => ChecklistItemForm;
  advice?: (advice: ChecklistAdviceForm) => ChecklistAdviceForm;
};

/** Minimal payer shape used to sort advice groups by payer name. */
type PayerOption = { id: string; name: Label };

const managerRoles: string[] = [SALES_MANAGER, AFTER_SALES_MANAGER];

/**
 * Existence methods
 */
export const hasItems = (checklists: ChecklistForm[] = []): boolean =>
  checklists.some(checklist => Boolean(checklist.items?.length));

/** Sort advices by answeredDate, items present in the "done" checklist should be the last  */
export const adviceSorter = (
  a: Pick<ChecklistAdviceForm, 'type' | 'answeredDate'>,
  b: Pick<ChecklistAdviceForm, 'type' | 'answeredDate'>,
): number => {
  if (a.type === CHECKLIST_DONE) {
    const DEFAULT_DATE = new Date();

    return b.type === CHECKLIST_DONE
      ? differenceInMilliseconds((b.answeredDate ?? DEFAULT_DATE) as Date, (a.answeredDate ?? DEFAULT_DATE) as Date)
      : 1; // If b isn't a CHECKLIST_DONE advice, it'll push a the end of pile
  }
  return differenceInMilliseconds(b.answeredDate as unknown as Date, a.answeredDate as unknown as Date);
};

export const isDealerChecklist = (checklist: Pick<ChecklistForm, 'dealerId'>): boolean =>
  checklist.dealerId !== undefined;

export const isGroupChecklist = (checklist: Pick<ChecklistForm, 'groupId' | 'dealerId'>): boolean =>
  checklist.groupId !== undefined && checklist.dealerId === undefined;

export const isChecklistEditable = (
  checklist: Pick<ChecklistForm, 'groupId' | 'dealerId'>,
  userRoles: string[]
): boolean => {
  const isManager = hasAccess(userRoles, managerRoles);

  if (isManager && isGroupChecklist(checklist)) {
    return false;
  }
  return !(isManager && isGroupChecklist(checklist));
};

/**
 * Build methods
 */
export const createNewChecklist = (
  name?: Label | string,
  type?: ChecklistType,
  interventionId?: string,
  _default = false,
) => {
  const key = uuidv4();
  return {
    name,
    items: [] as ChecklistItemForm[],
    type,
    default: _default,
    key, // key is correct, we cannot set an id otherwise backend will throw an error
    interventionId,
  };
};

export const createNewItem = (categoryName?: Label | string, others = true) => {
  const key = uuidv4();
  return {
    categoryId: key,
    categoryName,
    advices: [] as ChecklistAdviceForm[],
    others,
  };
};

export const createNewAdvice = (
  severity: ChecklistSeverity = SEVERITY.NONE,
  category?: string,
  name?: Label | string,
  coefficient = 1,
) => ({
  adviceId: uuidv4(),
  severity,
  accepted: false,
  isNew: true,
  category,
  adviceName: name,
  status: ADVICE_STATUS.PENDING,
  coefficient,
  medias: [] as AbstractMedia[],
  price: 0,
});

/**
 * Getter methods
 */
export const getItems = (checklists?: ChecklistForm | ChecklistForm[]): ChecklistItemForm[] =>
  (Array.isArray(checklists) ? checklists.flatMap(getItems) : checklists?.items ?? []);

export const getAdvicesFromItems = (items?: ChecklistItemForm[]): ChecklistAdviceForm[] =>
  items?.flatMap(({ advices = [] }) => advices) ?? [];

/**
 * Walks the checklist tree and returns every advice, optionally filtering checklists and mapping
 * each advice through `itemFormatter`. The checklist input is intentionally permissive (`C`) because
 * call sites pass react-final-form working copies and partial/loosely-typed objects; internally we
 * treat each entry as a {@link ChecklistForm}.
 */
export function getAdvices<C = ChecklistForm, R = ChecklistAdviceForm>(
  checklists: C | C[],
  checklistFilter?: ((checklist: C) => boolean) | null,
  itemFormatter?: (
    checklist: C,
    item: ChecklistItemForm,
    advice: ChecklistAdviceForm,
    itemIndex: number,
    adviceIndex: number,
  ) => R,
): R[] {
  const arrayChecklist = Array.isArray(checklists) ? checklists : [checklists];
  const filtered = typeof checklistFilter === 'function' ? arrayChecklist.filter(checklistFilter) : arrayChecklist;

  return filtered.flatMap(checklist => {
    const items = (checklist as unknown as ChecklistForm | undefined)?.items ?? [];
    return items.flatMap((item, itemIndex) =>
      (item?.advices ?? []).flatMap(
        (advice, adviceIndex) => (itemFormatter?.(checklist, item, advice, itemIndex, adviceIndex) ?? advice) as R,
      ),);
  });
}

const isDefaultChecklist = (checklist: ChecklistForm, type: ChecklistType): boolean =>
  Boolean(checklist.default) &&
  checklist.type === type &&
  !checklist.originTireScanId &&
  !checklist.originInterventionId &&
  !checklist.originAuditId &&
  !checklist.offeredChecklist;

export const getDefaultChecklist = (checklists: ChecklistForm[] | undefined, type: ChecklistType) =>
  checklists?.find(checklist => isDefaultChecklist(checklist, type));

// We don't want to check `originInterventionId` in SALES mode because we can duplicate checklists from the same intervention
export const isImported = (checklist: ChecklistForm, mode: string = TECHNICIAN) =>
  checklist.originAuditId || checklist.originTireScanId || (mode === TECHNICIAN && checklist.originInterventionId);

export const getDefaultItem = (checklist: { items?: ChecklistItemForm[] } = {}) =>
  (checklist.items || []).find(item => item.others === true);

export const getAnsweredAdvices = (
  checklist: ChecklistForm,
  basePaths: { checklists?: string; checklist: string },
) =>
  getAdvices(checklist, null, (_, item, advice, itemIndex, adviceIndex) => ({
    ...advice,
    index: adviceIndex,
    type: checklist.type,
    checklistId: checklist.id,
    categoryId: item.categoryId,
    allowRestrict: !checklist.selfServiceId,
    paths: getPaths(itemIndex, adviceIndex, basePaths),
  })).filter(({ type, status }) => type === CHECKLIST_DONE || status !== ADVICE_STATUS.PENDING);

/**
 * Get previously proposed works from checklists
 */
export const getPreviouslyProposedWorks = (checklists: ChecklistForm[]) =>
  checklists.reduce<ChecklistAdviceForm[]>((acc, checklist, index) => {
    const advices = getAdvices(checklist, null, (_, item, advice, itemIndex, adviceIndex) => ({
      ...advice,
      index: adviceIndex,
      type: checklist.type,
      checklistId: checklist.id,
      categoryId: item.categoryId,
      paths: getPaths(itemIndex, adviceIndex, {
        checklists: `checklists_${checklist.type}`,
        checklist: `checklists_${checklist.type}[${index}]`,
      }),
    }));
    return [...acc, ...advices];
  }, []);

/**
 * Update methods
 */

/* Removes checklists without items, and items without advices */
export const cleanChecklists = (checklists: ChecklistForm[]): ChecklistForm[] =>
  checklists.map(({ items = [], ...checklist }) => ({
    ...checklist,
    items: items.filter(({ advices = [] }) => advices.length > 0),
  }));

// TODO - With appointments, status has been added ("ACCEPTED", "REFUSED", "PENDING"), use that status after intervention have been migrated
export const getAcceptedAdvicesFromChecklists = (checklists: ChecklistForm[], typeFilter: ChecklistType) =>
  getAdvices(checklists, ({ type }) => type === typeFilter).filter(({ status }) => status === ADVICE_STATUS.ACCEPTED);

const mergeItem = (item1: ChecklistItemForm, item2: ChecklistItemForm): ChecklistItemForm => {
  const advices: ChecklistAdviceForm[] = [];
  (item1.advices || []).forEach(advice1 => {
    const adviceToMerge = (item2.advices || []).find(advice2 => advice2.adviceId === advice1.adviceId);
    if (adviceToMerge) {
      advices.push({ ...advice1, ...adviceToMerge });
    } else {
      advices.push({ ...advice1 });
    }
  });

  item2.advices.forEach(advice2 => {
    const isNewAdvice = !advices.map(advice => advice.adviceId).includes(advice2.adviceId);
    if (isNewAdvice) {
      advices.push(advice2);
    }
  });

  return { ...item1, ...item2, advices: compact(advices) };
};

export const mergeChecklist = (checklist1: ChecklistForm, checklist2: ChecklistForm): ChecklistForm => {
  const items: Array<ChecklistItemForm | undefined> = [];
  (checklist1.items || []).forEach(item1 => {
    const itemToMerge = (checklist2.items || []).find(item2 => item2.categoryId === item1.categoryId);
    if (itemToMerge) {
      const mergedItem = mergeItem(item1, itemToMerge);
      items.push(mergedItem);
    } else {
      items.push(item1);
    }
  });
  items.push((checklist2.items || []).find(item2 => !items.map(item => item?.categoryId).includes(item2.categoryId)));

  return {
    ...checklist1,
    ...checklist2,
    items: compact(items),
  };
};

export const generateI18nAdviceName = (languages: string[], adviceName: string): Label =>
  languages.reduce<Label>((acc, language) => {
    acc[language] = adviceName;
    return acc;
  }, {});

/**
 * Builds a label that stores the value only under the default language (en_us).
 *
 * Used at "usage" call-sites (interventions, keydeposit, self-service…) where a label is not
 * localized: instead of duplicating a fixed value across every language, we keep only the default
 * language to reduce database/network bloat. Configuration keeps full multi-language labels.
 *
 * @see BMWVID-27726
 */
export const generateDefaultLanguageLabel = (value: string): Label => generateI18nAdviceName([DEFAULT_LANGUAGE], value);

export const requiresStatusResolution = ({ status, severity }: Pick<ChecklistAdviceForm, 'status' | 'severity'>): boolean =>
  status === ADVICE_STATUS.PENDING && severity !== SEVERITY.UNDEFINED && severity !== SEVERITY.NONE;

export const resolveAdviceStatus = (
  advice: ChecklistAdviceForm,
  checklist: ChecklistForm,
  checklistGroupId: string,
  hasAcceptedItem: boolean,
) => {
  if (!requiresStatusResolution(advice)) {
    return advice.status;
  }

  const isInTargetGroup = checklist.checklistGroupId === checklistGroupId;
  if (!isInTargetGroup) {
    return ADVICE_STATUS.REFUSED;
  }

  const isAccepted =
    advice.accepted || // manually accepted
    checklist.type === CHECKLIST_DONE || // all advices in DONE checklists are accepted
    (checklist.type === CHECKLIST_DISCOUNT && hasAcceptedItem); // discount applies when any item in the group was accepted

  return isAccepted ? ADVICE_STATUS.ACCEPTED : ADVICE_STATUS.REFUSED;
};

export const setChecklistsAdvicesStatus = (checklists: ChecklistForm[], checklistGroupId: string): ChecklistForm[] => {
  const groupAdvices = getAdvices(checklists, checklist => checklist.checklistGroupId === checklistGroupId);
  const hasAcceptedItem = groupAdvices.some(({ accepted }) => accepted);

  return checklists.map(({ items = [], ...checklist }) => ({
    ...checklist,
    items: items.map(({ advices = [], ...item }) => ({
      ...item,
      advices: advices.map(advice => ({
        ...advice,
        discount: advice.discount ?? 0,
        status: resolveAdviceStatus(advice, checklist as ChecklistForm, checklistGroupId, hasAcceptedItem),
      })),
    })),
  }));
};

const getPaths = (
  itemIndex: number,
  adviceIndex: number,
  basePaths: { checklists?: string; checklist?: string } = {},
): ChecklistPaths => {
  const categories = `${basePaths.checklist}.items`;
  const category = `${categories}[${itemIndex}]`;
  const advices = `${category}.advices`;
  const advice = `${advices}[${adviceIndex}]`;

  return { ...basePaths, categories, category, advices, advice } as ChecklistPaths;
};

/**
 * Return last index of path
 */
export const getIndex = (path: string): number | undefined => {
  const item = [...path.matchAll(/([0-9]+)/g)].pop();
  return item ? parseInt(item[1], 10) : undefined;
};

export const getAnsweredChecklistGroup = (checklistGroups: ChecklistGroup[] = []): ChecklistGroup[] =>
  checklistGroups
    .filter(({ status } = {} as ChecklistGroup) => status !== CHECKLIST_STATUS.PENDING)
    .sort(a => (a.status === CHECKLIST_STATUS.ACCEPTED ? -1 : 0));

export const hasAnsweredAdvices = (advices: Pick<ChecklistAdviceForm, 'status'>[] = []): boolean =>
  (advices.length > 0 ? advices.every(({ status = ADVICE_STATUS.PENDING }) => status !== ADVICE_STATUS.PENDING) : false);

export const groupAdvicesByPayerId = (
  advices: ChecklistAdviceForm[],
  { translate, payers = [] }: { translate: (label: Label) => string; payers?: PayerOption[] },
): Array<[string, ChecklistAdviceForm[]]> => {
  const hasCustomerPayer = advices.some(({ payerId }) => !payerId);
  const initialValue: Record<string, ChecklistAdviceForm[]> = hasCustomerPayer ? { [DEFAULT_PAYER_ID]: [] } : {};

  const groupedAdvices = advices.reduce<Record<string, ChecklistAdviceForm[]>>((acc, advice) => {
    const { payerId = DEFAULT_PAYER_ID } = advice;
    return {
      ...acc,
      [payerId]: [...(acc[payerId] ?? []), advice],
    };
  }, initialValue);

  return Object.entries(groupedAdvices).sort(([payerIdA], [payerIdB]) => {
    if (payerIdA === DEFAULT_PAYER_ID) {
      return 1;
    }
    if (payerIdB === DEFAULT_PAYER_ID) {
      return -1;
    }

    const aPayer = payers.find(payer => payer.id === payerIdA);
    const bPayer = payers.find(payer => payer.id === payerIdB);

    const aPayerName = aPayer ? translate(aPayer.name) : '';
    const bPayerName = bPayer ? translate(bPayer.name) : '';

    return aPayerName.localeCompare(bPayerName);
  });
};

export const formatImportChecklists = (checklists: ChecklistForm[], formatters: ChecklistFormatters): ChecklistForm[] =>
  checklists.map(checklist => {
    const formattedChecklist = formatters?.checklist?.(checklist) ?? checklist;
    return {
      ...formattedChecklist,
      originChecklistId: checklist.id,
      items: formattedChecklist.items.map(item => {
        const formattedItem = formatters?.item?.(item) ?? item;
        return {
          ...formattedItem,
          advices: formattedItem.advices.map(advice => {
            const formattedAdvice = formatters?.advice?.(advice) ?? advice;
            return formattedAdvice;
          }),
        };
      }),
    };
  });

export const isAdviceAccepted = (status?: string): boolean =>
  status === ADVICE_STATUS.ACCEPTED || status === ADVICE_STATUS.DEPOSIT_PAID || status === ADVICE_STATUS.PAID;
